import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
import type { AuditFields, Page } from './types';

export type InspectionStatus = 'PROPOSAL' | 'PLANNING' | 'EXECUTION' | 'EVALUATION' | 'FINISHED';
export const STATUSES: InspectionStatus[] = ['PROPOSAL', 'PLANNING', 'EXECUTION', 'EVALUATION', 'FINISHED'];

export interface SubjectRef {
  id: string;
  name: string;
  eduid: string;
  municipality: string;
  region: string;
  district: string | null;
  type: string;
}

export interface Member {
  inspectorId: string;
  displayName: string;
  email: string;
  phone: string;
  leader: boolean;
}

export interface StatusChange {
  from: InspectionStatus | null;
  to: InspectionStatus;
  changedAt: string;
  changedByName: string;
  byRole: 'ADMIN' | 'LEADER' | 'MEMBER' | 'SYSTEM';
  reason: string | null;
}

export interface Permissions {
  admin: boolean;
  member: boolean;
  leader: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canFillCurrentForm: boolean;
  allowedTransitions: InspectionStatus[];
  canGenerateReport: boolean;
}

export interface Inspection extends AuditFields {
  id: string;
  version: number;
  number: string;
  name: string;
  /** Inspection type code ("1"…"5"). */
  type: string;
  /** Inspection task code of the type ("2.3"); null when the type has no tasks. */
  task: string | null;
  /** The inspected subject, chosen in the wizard; null only for older inspections that never named one. */
  subject: SubjectRef | null;
  dateFrom: string;
  dateTo: string;
  status: InspectionStatus;
  members: Member[];
  statusHistory: StatusChange[];
  permissions: Permissions;
  reportAvailable: boolean;
}

export interface InspectionListItem {
  id: string;
  number: string;
  name: string;
  subject: SubjectRef | null;
  type: string;
  task: string | null;
  dateFrom: string;
  dateTo: string;
  status: InspectionStatus;
  leaderName: string | null;
}

export interface InspectionRequest {
  name: string;
  subjectId: string;
  type: string;
  task: string | null;
  dateFrom: string;
  dateTo: string;
  members: { inspectorId: string; leader: boolean }[];
  version?: number;
}

export interface InspectionQuery {
  q?: string;
  status?: string;
  type?: string;
  task?: string;
  subjectId?: string;
  from?: string;
  to?: string;
  page: number;
  size: number;
  sort: string;
}

export function useInspections(query: InspectionQuery) {
  return useQuery({
    queryKey: ['inspections', 'list', query],
    queryFn: ({ signal }) => api<Page<InspectionListItem>>('/inspections', { query: { ...query }, signal }),
    placeholderData: keepPreviousData,
  });
}

export function useInspectionStats() {
  return useQuery({
    queryKey: ['inspections', 'stats'],
    queryFn: () => api<{ counts: Record<InspectionStatus, number>; total: number }>('/inspections/stats'),
  });
}

export function useInspection(id: string | undefined) {
  return useQuery({ queryKey: ['inspections', id], queryFn: () => api<Inspection>(`/inspections/${id}`), enabled: Boolean(id) });
}

export function useCreateInspection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (request: InspectionRequest) => api<Inspection>('/inspections', { method: 'POST', body: request }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['inspections'] }),
  });
}

export function useUpdateInspection(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (request: InspectionRequest) => api<Inspection>(`/inspections/${id}`, { method: 'PUT', body: request, version: request.version }),
    onSuccess: (inspection) => {
      qc.setQueryData(['inspections', id], inspection);
      void qc.invalidateQueries({ queryKey: ['inspections'] });
    },
  });
}

export function useDeleteInspection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (i: { id: string; version: number }) => api<void>(`/inspections/${i.id}`, { method: 'DELETE', version: i.version }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['inspections'] }),
  });
}
